import React, { useState, useEffect } from 'react';
import { MessageSquare, AlertCircle, CheckCircle, Search, Building, Eye, Trash2, Check, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminGlobalLayout from '../../components/AdminGlobalLayout';
import AdminDataTable from '../../components/AdminDataTable';

interface Testimonial {
  id: string;
  title: string;
  content: string;
  user_id: string;
  created_at: string;
  status: 'pendente' | 'aprovado' | 'rejeitado';
  approved_at: string | null;
  approved_by: string | null;
  edit_history: any[];
  igreja_id: string;
  igreja_nome?: string;
  user_name?: string;
}

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [igrejas, setIgrejas] = useState<Igreja[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [viewingTestimonial, setViewingTestimonial] = useState<Testimonial | null>(null);
  const [testimonialToDelete, setTestimonialToDelete] = useState<Testimonial | null>(null);
  
  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTestimonials, setFilteredTestimonials] = useState<Testimonial[]>([]);
  const [selectedIgreja, setSelectedIgreja] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    fetchTestimonials();
    fetchIgrejas();
  }, []);

  useEffect(() => {
    // Filter and sort testimonials
    let filtered = [...testimonials];
    
    // Apply igreja filter
    if (selectedIgreja) {
      filtered = filtered.filter(testimonial => testimonial.igreja_id === selectedIgreja);
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(testimonial => testimonial.status === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(testimonial => 
        testimonial.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        testimonial.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        testimonial.igreja_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        testimonial.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortField === 'igreja_nome') {
          aValue = a.igreja_nome || '';
          bValue = b.igreja_nome || '';
        } else if (sortField === 'user_name') {
          aValue = a.user_name || '';
          bValue = b.user_name || '';
        } else {
          aValue = a[sortField as keyof Testimonial];
          bValue = b[sortField as keyof Testimonial];
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    setFilteredTestimonials(filtered);
  }, [testimonials, searchTerm, sortField, sortOrder, selectedIgreja, statusFilter]);

  async function fetchTestimonials() {
    try {
      setLoading(true);
      
      // Fetch testimonials
      const { data: testimonialsData, error: testimonialsError } = await supabase
        .from('testimonials')
        .select('*');
      
      if (testimonialsError) throw testimonialsError;
      
      // Fetch igrejas to get names
      const { data: igrejasData, error: igrejasError } = await supabase
        .from('igrejas')
        .select('id, nome');
      
      if (igrejasError) throw igrejasError;
      
      // Fetch users to get names
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, user_metadata');
      
      if (usersError) throw usersError;
      
      // Map igreja and user names to testimonials
      const testimonialsWithNames = testimonialsData?.map(testimonial => {
        const igreja = igrejasData?.find(i => i.id === testimonial.igreja_id);
        const user = usersData?.find(u => u.id === testimonial.user_id);
        return {
          ...testimonial,
          igreja_nome: igreja?.nome || 'Sem igreja',
          user_name: user?.user_metadata?.name || 'Usuário Anônimo'
        };
      }) || [];
      
      setTestimonials(testimonialsWithNames);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar testemunhos');
    } finally {
      setLoading(false);
    }
  }

  async function fetchIgrejas() {
    try {
      const { data, error } = await supabase
        .from('igrejas')
        .select('id, nome, cidade, estado')
        .order('nome');

      if (error) throw error;
      setIgrejas(data || []);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar igrejas');
    }
  }

  const handleApprove = async (testimonial: Testimonial) => {
    try {
      setError('');
      setSuccess('');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');
      
      // Update testimonial status
      const { error } = await supabase
        .from('testimonials')
        .update({ 
          status: 'aprovado',
          approved_at: new Date().toISOString(),
          approved_by: user.id
        })
        .eq('id', testimonial.id);
      
      if (error) throw error;
      
      setSuccess('Testemunho aprovado com sucesso!');
      
      // Refresh data
      fetchTestimonials();
    } catch (error: any) {
      setError(error.message || 'Erro ao aprovar testemunho');
    }
  };

  const handleReject = async (testimonial: Testimonial) => {
    try {
      setError('');
      setSuccess('');
      
      // Update testimonial status
      const { error } = await supabase
        .from('testimonials')
        .update({ status: 'rejeitado' })
        .eq('id', testimonial.id);
      
      if (error) throw error;
      
      setSuccess('Testemunho rejeitado com sucesso!');
      
      // Refresh data
      fetchTestimonials();
    } catch (error: any) {
      setError(error.message || 'Erro ao rejeitar testemunho');
    }
  };

  const handleDelete = async () => {
    if (!testimonialToDelete) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Delete testimonial
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', testimonialToDelete.id);
      
      if (error) throw error;
      
      setSuccess('Testemunho excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setTestimonialToDelete(null);
      
      // Refresh data
      fetchTestimonials();
    } catch (error: any) {
      setError(error.message || 'Erro ao excluir testemunho');
    }
  };

  const handleSortingChange = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'aprovado': return 'Aprovado';
      case 'rejeitado': return 'Rejeitado';
      default: return 'Pendente';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aprovado': return 'bg-green-100 text-green-800';
      case 'rejeitado': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const columns = [
    {
      header: 'Título',
      accessorKey: 'title',
      enableSorting: true,
      cell: (info: { row: Testimonial }) => (
        <div>
          <div className="font-medium text-gray-900">{info.row.title}</div>
          <div className="text-sm text-gray-500 line-clamp-1">{info.row.content}</div>
        </div>
      )
    },
    {
      header: 'Autor',
      accessorKey: 'user_name',
      enableSorting: true,
    },
    {
      header: 'Igreja',
      accessorKey: 'igreja_nome',
      enableSorting: true,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableSorting: true,
      cell: (info: { row: Testimonial }) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(info.row.status)}`}>
          {getStatusLabel(info.row.status)}
        </span>
      )
    },
    {
      header: 'Data',
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (info: { row: Testimonial }) => (
        <div>{formatDate(info.row.created_at)}</div>
      )
    },
    {
      header: 'Ações',
      accessorKey: 'id',
      cell: (info: { row: Testimonial }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => {
              setViewingTestimonial(info.row);
              setIsViewModalOpen(true);
            }}
            className="p-1 text-brand-teal hover:text-brand-teal/80 rounded-full"
            title="Visualizar"
          >
            <Eye className="h-5 w-5" />
          </button>
          {info.row.status === 'pendente' && (
            <>
              <button
                onClick={() => handleApprove(info.row)}
                className="p-1 text-green-500 hover:text-green-600 rounded-full"
                title="Aprovar"
              >
                <Check className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleReject(info.row)}
                className="p-1 text-red-500 hover:text-red-600 rounded-full"
                title="Rejeitar"
              >
                <X className="h-5 w-5" />
              </button>
            </>
          )}
          <button
            onClick={() => {
              setTestimonialToDelete(info.row);
              setIsDeleteModalOpen(true);
            }}
            className="p-1 text-red-500 hover:text-red-600 rounded-full"
            title="Excluir"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  // Calculate pagination
  const pageCount = Math.ceil(filteredTestimonials.length / pageSize);
  const paginatedData = filteredTestimonials.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  return (
    <AdminGlobalLayout title="Testemunhos">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="igreja_filter" className="block text-sm font-medium text-gray-700 mb-1">
            Filtrar por Igreja
          </label>
          <select
            id="igreja_filter"
            value={selectedIgreja}
            onChange={(e) => setSelectedIgreja(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
          >
            <option value="">Todas as Igrejas</option>
            {igrejas.map((igreja) => (
              <option key={igreja.id} value={igreja.id}>
                {igreja.nome} - {igreja.cidade}/{igreja.estado}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="status_filter" className="block text-sm font-medium text-gray-700 mb-1">
            Filtrar por Status
          </label>
          <select
            id="status_filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
          >
            <option value="">Todos os Status</option>
            <option value="pendente">Pendentes</option>
            <option value="aprovado">Aprovados</option>
            <option value="rejeitado">Rejeitados</option>
          </select>
        </div>
      </div>

      <AdminDataTable
        data={paginatedData}
        columns={columns}
        title="Testemunhos"
        subtitle={`Total de ${filteredTestimonials.length} testemunhos`}
        loading={loading}
        pagination={{
          pageIndex,
          pageSize,
          pageCount,
          onPageChange: setPageIndex
        }}
        sorting={{
          field: sortField,
          order: sortOrder,
          onSortingChange: handleSortingChange
        }}
        filtering={{
          globalFilter: searchTerm,
          onGlobalFilterChange: setSearchTerm
        }}
        enableExport={true}
        exportFileName="testemunhos"
      />

      {/* View Testimonial Modal */}
      {isViewModalOpen && viewingTestimonial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-semibold text-brand-teal">{viewingTestimonial.title}</h2>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Autor</h3>
                  <p className="mt-1 text-lg font-medium text-gray-900">{viewingTestimonial.user_name}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(viewingTestimonial.status)}`}>
                  {getStatusLabel(viewingTestimonial.status)}
                </span>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Igreja</h3>
                <p className="mt-1 text-lg text-gray-900">{viewingTestimonial.igreja_nome}</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Data de Criação</h3>
                  <p className="mt-1 text-lg text-gray-900">{formatDate(viewingTestimonial.created_at)}</p>
                </div>
                
                {viewingTestimonial.approved_at && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Data de Aprovação</h3>
                    <p className="mt-1 text-lg text-gray-900">{formatDate(viewingTestimonial.approved_at)}</p>
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Conteúdo</h3>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-900 whitespace-pre-line">{viewingTestimonial.content}</p>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <div className="flex gap-2">
                  {viewingTestimonial.status === 'pendente' && (
                    <>
                      <button
                        onClick={() => {
                          handleApprove(viewingTestimonial);
                          setIsViewModalOpen(false);
                        }}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                      >
                        <Check className="h-4 w-4" />
                        Aprovar
                      </button>
                      <button
                        onClick={() => {
                          handleReject(viewingTestimonial);
                          setIsViewModalOpen(false);
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Rejeitar
                      </button>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && testimonialToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir o testemunho <strong>"{testimonialToDelete.title}"</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setTestimonialToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminGlobalLayout>
  );
}